import {
  isOutboundReferralStatus,
  receiptSeverity,
  type OutboundReferralStatus,
} from './referralReceipt';

export type ReceiptTone = 'negative' | 'positive' | 'waiting' | 'unknown';

export type ReceiptBadge = {
  /** i18n key under patients.hospital_referral. */
  key: string;
  /** Danish fallback text. */
  fallback: string;
  tone: ReceiptTone;
};

/** Clinician-facing receipt badge for a sent referral (XREF01/02/06). */
export function referralReceiptBadge(status: string): ReceiptBadge {
  if (!isOutboundReferralStatus(status)) {
    return { key: 'receipt_badge_unknown', fallback: 'Ukendt status', tone: 'unknown' };
  }
  switch (status) {
    case 'built':
      return { key: 'receipt_badge_waiting', fallback: 'Afventer kvittering', tone: 'waiting' };
    case 'xctl03':
      return { key: 'receipt_badge_positive', fallback: 'Positiv kvittering', tone: 'positive' };
    case 'xctl02':
      return { key: 'receipt_badge_negative', fallback: 'Negativ kvittering', tone: 'negative' };
    case 'xctl01':
      return {
        key: 'receipt_badge_negative_vans',
        fallback: 'Negativ VANS-kvittering',
        tone: 'negative',
      };
    default: {
      const unexpected: never = status;
      return unexpected;
    }
  }
}

export const RECEIPT_TONE_CLASS: Record<ReceiptTone, string> = {
  negative: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  positive: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200',
  waiting: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200',
  unknown: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200',
};

function severityOf(status: string): number {
  return isOutboundReferralStatus(status) ? receiptSeverity(status as OutboundReferralStatus) : -1;
}

/**
 * Order for the patient summary: negative receipts first, then referrals
 * still waiting, then positive ones; newest first within each group.
 */
export function orderForPatientSummary<T extends { status: string; created_at: string }>(
  rows: readonly T[],
): T[] {
  const rank = (status: string): number => {
    const severity = severityOf(status);
    if (severity === 2) return 0;
    if (severity === 0) return 1;
    if (severity === 1) return 2;
    return 3;
  };
  return [...rows].sort(
    (a, b) => rank(a.status) - rank(b.status) || b.created_at.localeCompare(a.created_at),
  );
}
